import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function PriceLists() {
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [entries, setEntries] = useState([]);
  const [newItem, setNewItem] = useState({ item_id: "", price: 0 });

  useEffect(() => {
    api.get("/parties").then(({ data }) => setParties(data));
    api.get("/items").then(({ data }) => setItems(data));
  }, []);

  useEffect(() => {
    if (!partyId) return setEntries([]);
    api.get(`/pricelist/${partyId}`).then(({ data }) => setEntries(data));
  }, [partyId]);

  const party = useMemo(() => parties.find((p) => p.party_id === partyId), [parties, partyId]);

  const addPrice = async () => {
    if (!newItem.item_id || !partyId) return toast.error("Choose party and item");
    await api.post("/pricelist", { party_id: partyId, item_id: newItem.item_id, price: newItem.price });
    setNewItem({ item_id: "", price: 0 });
    toast.success("Price saved");
    const { data } = await api.get(`/pricelist/${partyId}`);
    setEntries(data);
  };

  const del = async (id) => {
    await api.delete(`/pricelist/${id}`);
    const { data } = await api.get(`/pricelist/${partyId}`);
    setEntries(data);
  };

  return (
    <div>
      <PageHeader title="Price Lists" subtitle="Party-specific pricing for customers and suppliers" />
      <div className="px-8 py-6 space-y-6">
        <Card className="p-6 border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Party (Customer or Supplier)</Label>
              <Select value={partyId} onValueChange={setPartyId}>
                <SelectTrigger data-testid="pricelist-party-select"><SelectValue placeholder="Select a party" /></SelectTrigger>
                <SelectContent>
                  {parties.map((p) => (
                    <SelectItem key={p.party_id} value={p.party_id}>
                      {p.name} — {p.kind}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {party && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Add Item</Label>
                  <Select value={newItem.item_id} onValueChange={(v) => setNewItem({ ...newItem, item_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                    <SelectContent>
                      {items.map((i) => (
                        <SelectItem key={i.item_id} value={i.item_id}>{i.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Price (₹)</Label>
                  <div className="flex gap-2">
                    <Input type="number" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: +e.target.value })} />
                    <Button onClick={addPrice} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-price-btn">Save</Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Card>

        {party && (
          <Card className="border-slate-200 overflow-hidden">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Part No.</TableHead>
                  <TableHead>Item / Description</TableHead>
                  <TableHead>HSN Code</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-right">GST%</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">No prices yet.</TableCell></TableRow>
                )}
                {entries.map((e) => (
                  <TableRow key={e.pricelist_id}>
                    <TableCell className="font-mono text-xs">{e.sku || "—"}</TableCell>
                    <TableCell className="font-medium">{e.item_name}</TableCell>
                    <TableCell className="font-mono text-xs">{e.hsn || "—"}</TableCell>
                    <TableCell>{e.unit}</TableCell>
                    <TableCell className="text-right number-cell">{e.gst_rate}%</TableCell>
                    <TableCell className="text-right number-cell font-semibold">₹{Number(e.price).toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => del(e.pricelist_id)}>
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </div>
  );
}
